import Button from "@/components/Button/Button";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import PencilIcon from "@/components/SVGComponents/PencilIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import { combineClasses } from "@/lib/utils";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/dateFormat";
import type { SubscriptionPlan } from "@/services/api/subscription";
import styles from "./PlanCard.module.scss";

// The real `GET /subscription-plan` model (services/api/subscription.ts)
// has no "status" (live/invite-only), member-count, or feature-list field —
// this card only shows what the API actually returns.
export type Plan = SubscriptionPlan;

export interface PlanCardProps {
  plan: Plan;
  onEdit: (plan: Plan) => void;
  // Subscriptions.tsx owns the confirm modal + delete call, same as `onEdit`.
  onDelete: (plan: Plan) => void;
}

const formatPlanDate = (isoDate: string) =>
  formatDate(isoDate, { locale: "en-IN", day: "numeric", month: "short", year: "numeric", fallback: "-" });

const PlanCard = ({ plan, onEdit, onDelete }: PlanCardProps) => {
  const { name, amount, planDurationMonth, tag, description, createdAt, updatedAt } = plan;
  const durationLabel = planDurationMonth === 1 ? "1 month" : `${planDurationMonth} months`;
  // The API sends `\r\n`/`\n` inside `description` (its own "WHAT IT
  // INCLUDES\n\n✓ ..." checklist formatting) — normalize both to `\n` and
  // let `white-space: pre-line` (PlanCard.module.scss) turn them into
  // real line breaks instead of collapsing them into one line.
  const displayDescription = (description || "No description provided.").replace(/\r\n/g, "\n");

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.name}>{name}</h3>
        {tag && <StatusBadge label={tag} tone="neutral" />}
      </div>

      <div className={styles.priceBlock}>
        <span className={styles.price}>{formatINR(amount)}</span>
        <span className={styles.priceCaption}>every {durationLabel}</span>
      </div>

      <div className={styles.membersBox}>
        <div className={styles.membersStat}>
          <span className={styles.membersValue}>{formatPlanDate(createdAt)}</span>
          <span className={styles.membersLabel}>created</span>
        </div>
        <div className={combineClasses(styles.membersStat, styles.membersStatRight)}>
          <span className={styles.membersValue}>{formatPlanDate(updatedAt)}</span>
          <span className={styles.membersLabel}>last updated</span>
        </div>
      </div>

      <p className={styles.description}>{displayDescription}</p>

      <div className={styles.actions}>
        <div className={styles.editButtonWrap}>
          <Button outlined onClick={() => onEdit(plan)}>
            <span className={styles.editButtonContent}>
              <PencilIcon />
              EDIT PLAN
            </span>
          </Button>
        </div>
        <button
          type="button"
          className={styles.deleteButton}
          onClick={() => onDelete(plan)}
          aria-label={`Delete ${name}`}
          title="Delete plan"
        >
          <CrossIcon withBg={false} width={16} height={16} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
};

export default PlanCard;
